import { Modal, ModalBody, ModalCloseButton, ModalContent, ModalOverlay } from '@chakra-ui/react';
import { StandardSnack } from '@sketch-note/design-system/src/components/snackbar';
import {
  CustomDateIcon,
  CustomEmailIcon,
  CustomMultiselectIcon,
  CustomNumberIcon,
  CustomPersonIcon,
  CustomPhoneIcon,
  CustomSelectIcon,
  CustomTextIcon,
  CustomUrlIcon,
  ErrorIcon,
} from '@sketchnote/icons';
import { Button, Checkbox, Switch } from '@sketchnote/shared-components';
import cn from 'classnames';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { useTasksStore } from '../store/useTasksStore';
import { CustomFieldType, TaskT } from '../types/tasks';

export const TaskTemplateSave = ({
  task,
  saveTemplateModal,
  setSaveTemplateModal,
}: {
  task: TaskT;
  saveTemplateModal: boolean;
  setSaveTemplateModal: Dispatch<SetStateAction<boolean>>;
}) => {
  const [templateName, setTemplateName] = useState('');
  const [custom, setCustom] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [errorModalIsOpen, setErrorModalIsOpen] = useState(false);
  const [customFields, setCustomFields] = useState<any>([]);
  // const { onSaveTemplate } = useTasksStore();

  const onCancel = () => {
    setSaveTemplateModal(false);
  };

  const onSave = () => {
    if (templateName === '') setErrorModalIsOpen(true);
    else {
      let selectedFields: CustomFieldType[] = [];
      if (custom) {
        //if custom is selected then set ischecked true for all the customfields and save template.
        selectedFields = customFields.map((item) => ({ ...item, isChecked: true }));
      } else {
        /* send selected customFields for task template*/
        selectedFields = customFields.filter((item) => item.isChecked == true);
      }
      // onSaveTemplate(templateName, selectedFields, task.id);
      setSaveTemplateModal(false);
    }
  };

  const handleErrorModal = () => {
    setErrorModalIsOpen(false);
    setIsFocused(true);
  };

  return (
    <>
      <Modal isOpen={saveTemplateModal} onClose={() => setSaveTemplateModal(false)}>
        <ModalOverlay />
        <ModalContent
          maxWidth="51.6rem"
          maxHeight="54.3rem"
          borderRadius="0.8rem"
          className="bg-white static rounded-lg"
          margin="auto"
        >
          <ModalCloseButton />
          <ModalBody className="flex ">
            <div className="w-full p-32">
              <span className="text-lg font-medium text-gray-999 mb-16">Create task template</span>
              <p className="text-gray-500 text-xss mb-24">
                Choose the task details that you want to include in the template.
              </p>
              <div className="flex flex-col">
                <span className="mb-14 text-gray-500 text-sm">Template Name</span>
                <input
                  className={cn('bg-gray-100 text-gray-999 w-420 h-40 border rounded-sm p-10', {
                    'border-gray-200': isFocused,
                  })}
                  onChange={(e) => {
                    setTemplateName(e.target.value);
                  }}
                  onFocus={(e) => {
                    setIsFocused(true);
                  }}
                ></input>
              </div>
              <div className="flex justify-between mt-25">
                <div className="">
                  <div className="flex items-center justify-between">
                    <div className="flex mr-10">
                      <Checkbox
                        wrapperClass="mr-8"
                        checked={custom}
                        isDisabled={false}
                        color={'#627FE5'}
                        size="small"
                        onChange={(e) => {
                          setCustom(e);
                        }}
                      />
                      <div className="text-sm text-gray-600 font-regular">Custom task fields</div>
                    </div>
                    {/* <div className="text-xs text-gray-600 font-regular pr-18">
                      Copy field values
                    </div> */}
                  </div>
                  <CustomFieldSection
                    fields={task?.fields}
                    custom={custom}
                    setCustom={setCustom}
                    customFields={customFields}
                    setCustomFields={setCustomFields}
                  />
                </div>
              </div>
              <div className="pt-30 flex justify-end">
                <Button
                  onClick={onCancel}
                  status="secondary"
                  className="mr-16 min-w-100 flex justify-center"
                >
                  Cancel
                </Button>
                <Button onClick={onSave} status="primary" className="min-w-100 flex justify-center">
                  Save template
                </Button>
              </div>
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>
      <Modal isOpen={errorModalIsOpen} onClose={handleErrorModal}>
        <ModalContent style={{ width: '40rem', borderRadius: '10px', marginTop: '65rem' }}>
          <StandardSnack icon={<ErrorIcon />} text="Please enter template name" />
        </ModalContent>
      </Modal>
    </>
  );
};

export const CustomFieldSection = ({
  fields,
  custom,
  setCustom,
  customFields,
  setCustomFields,
}: {
  fields: any;
  custom: boolean;
  setCustom: Dispatch<SetStateAction<boolean>>;
  customFields: any;
  setCustomFields: Dispatch<SetStateAction<any>>;
}) => {
  useEffect(() => {
    const CreateCustomArray = () => {
      const result = fields.map((item, index) => ({
        id: item.fieldId,
        type: item.type,
        label: item.label,
        isChecked: false,
        isCopy: false,
      }));

      setCustomFields(result);
    };
    CreateCustomArray();
  }, []);

  return (
    <div className="w-420 h-160 max-h-168 bg-gray-100 mt-8 pt-8 pl-20 pr-14 rounded-md overflow-y-scroll">
      {customFields.map((field, index) => (
        <div className="flex items-center justify-between pb-8" key={index}>
          <div className="flex items-center">
            <Checkbox
              wrapperClass="mr-8"
              checked={custom ? true : field.isChecked}
              isDisabled={custom}
              color={'#627FE5'}
              size="small"
              onChange={(e) => {
                setCustomFields((prev) => {
                  const item = prev.find((item) => item.id === field.id);
                  item && (item.isChecked = e);
                  !item.isChecked && (item.isCopy = false);
                  return [...prev];
                });
              }}
            />
            {field.type === 'text' ? (
              <CustomTextIcon wrapperClass="text-gray-400 mr-4" size={20} />
            ) : field.type === 'number' ? (
              <CustomNumberIcon wrapperClass="text-gray-400 mr-4" size={20} />
            ) : field.type === 'email' ? (
              <CustomEmailIcon wrapperClass="text-gray-400 mr-4" size={20} />
            ) : field.type === 'select' ? (
              <CustomSelectIcon wrapperClass="text-gray-400 mr-4" size={20} />
            ) : field.type === 'multiselect' ? (
              <CustomMultiselectIcon wrapperClass="text-gray-400 mr-4" size={20} />
            ) : field.type === 'phone' ? (
              <CustomPhoneIcon wrapperClass="text-gray-400 mr-4" size={20} />
            ) : field.type === 'url' ? (
              <CustomUrlIcon wrapperClass="text-gray-400 mr-4" size={20} />
            ) : field.type === 'person' ? (
              <CustomPersonIcon wrapperClass="text-gray-400 mr-4" size={20} />
            ) : field.type === 'date' ? (
              <CustomDateIcon wrapperClass="text-gray-400 mr-4" size={20} />
            ) : null}
            <p className="text-sm text-gray-600 font-regular">{field.label}</p>
          </div>
          {/* <Switch
            checked={field.isCopy}
            onChange={(e) => {
              setCustomFields((prev) => {
                const item = prev.find((item) => item.id === field.id);
                item && (item.isCopy = e.target.checked);
                return [...prev];
              });
            }}
          /> */}
        </div>
      ))}
    </div>
  );
};
